
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ReactFlow, { 
    Controls, 
    Background, 
    useNodesState, 
    useEdgesState, 
    Handle, 
    Position 
} from 'reactflow';
import dagre from 'dagre';
import 'reactflow/dist/style.css'; // Importa o CSS base do ReactFlow

import api from '../../services/api';
import './Organograma.css'; // Nosso CSS customizado
import { FaSitemap, FaUsers, FaBuilding } from 'react-icons/fa';

// --- Spinner ---
const LoadingSpinner = () => (
    <div className="spinner-overlay-light">
        <div className="spinner-light"></div>
    </div>
);

// =================================================================
// --- Card Customizado (O "Capricho") ---
// =Cria um "nó" de organograma personalizado
// =================================================================
const CustomNode = ({ data }) => {
    // Fallback para foto de perfil
    const getInitials = (name) => {
        if (!name) return '??';
        const names = name.split(' ');
        const initials = names.map(n => n[0]).join('');
        return initials.substring(0, 2).toUpperCase();
    };

    const formatarNome = (nomeCompleto) => {
        if (!nomeCompleto) return 'Sem Nome';
        const parts = nomeCompleto.trim().split(' '); // .trim() para segurança
        
        // Se o nome for único (ex: "Admin")
        if (parts.length === 1) {
            return parts[0]; 
        }
        
        const primeiroNome = parts[0];
        const ultimoNome = parts[parts.length - 1];
        
        // Se o nome já for curto (ex: "Luciano Bivar"), retorna ele mesmo
        if (parts.length === 2) {
            return nomeCompleto;
        }

        return `${primeiroNome} ${ultimoNome}`;
    };

    return (
        <div className="organograma-node">
            <Handle type="target" position={Position.Top} className="organograma-handle" />
            
            <div className="node-content">
                {data.foto_perfil ? (
                    <img src={data.foto_perfil} alt={data.nome} className="node-avatar" />
                ) : (
                    <div className="node-avatar-fallback">{getInitials(data.nome)}</div>
                )}
                <div className="node-info">
                    <div className="node-nome">{data.nome}</div>
                    <div className="node-cargo">{data.cargo}</div>
                </div>
            </div>
            
            <Handle type="source" position={Position.Bottom} className="organograma-handle" />
        </div>
    );
};

// Mapeia nosso 'CustomNode' para o ReactFlow
const nodeTypes = {
    custom: CustomNode,
};

// =================================================================
// --- Lógica de Layout (Dagre) ---
// Calcula automaticamente as posições X e Y dos nós
// =================================================================
const dagreGraph = new dagre.graphlib.Graph();
dagreGraph.setDefaultEdgeLabel(() => ({}));
const nodeWidth = 260;  // Largura do nosso CustomNode
const nodeHeight = 80; // Altura do nosso CustomNode

const getLayoutedElements = (nodes, edges) => {
    dagreGraph.setGraph({ rankdir: 'TB', nodesep: 40, ranksep: 80 }); // Top-to-Bottom, com espaçamento

    nodes.forEach((node) => {
        dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
    });

    edges.forEach((edge) => {
        dagreGraph.setEdge(edge.source, edge.target);
    });

    dagre.layout(dagreGraph);

    const layoutedNodes = nodes.map((node) => {
        const nodeWithPosition = dagreGraph.node(node.id);
        node.position = {
            x: nodeWithPosition.x - (nodeWidth / 2),
            y: nodeWithPosition.y - (nodeHeight / 2),
        };
        return node;
    });

    return { nodes: layoutedNodes, edges };
};

// =================================================================
// --- Componente Principal da Página ---
// =================================================================
const Organograma = () => {
    const [loading, setLoading] = useState(true);
    const [allColaboradores, setAllColaboradores] = useState([]);
    const [areas, setAreas] = useState([]);
    const [selectedArea, setSelectedArea] = useState('Todos');

    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);

    // 1. Busca Áreas e Colaboradores na primeira carga
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const [colabRes, areasRes] = await Promise.all([
                    api.get('/api/rh/organograma/colaboradores'),
                    api.get('/api/rh/organograma/areas')
                ]);
                setAllColaboradores(colabRes.data);
                setAreas(['Todos', ...areasRes.data]); // Adiciona "Todos" no início
            } catch (error) {
                console.error("Erro ao buscar dados do organograma:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    // 2. (A MÁGICA) Atualiza os Nós e Arestas quando a Área ou os Dados mudam
    useEffect(() => {
        if (allColaboradores.length === 0) return;

        // Filtra os colaboradores pela área selecionada
        const colaboradoresFiltrados = (selectedArea === 'Todos')
            ? allColaboradores
            : allColaboradores.filter(c => c.area === selectedArea);
        
        // Mapeia os colaboradores para o formato de "Nós" (Nodes) do ReactFlow
        const initialNodes = colaboradoresFiltrados.map(colab => ({
            id: colab.id.toString(),
            type: 'custom', // Nosso nó personalizado!
            data: { 
                nome: colab.nome, 
                cargo: colab.cargo, 
                foto_perfil: colab.foto_perfil 
            },
            position: { x: 0, y: 0 } // Posição inicial (será calculada)
        }));

        // Cria as "Arestas" (Edges) - as linhas de conexão
        const initialEdges = colaboradoresFiltrados
            .filter(c => c.lider_id) // Apenas quem tem líder
            .map(c => ({
                id: `e-${c.id}-${c.lider_id}`,
                source: c.lider_id.toString(), // De: Líder
                target: c.id.toString(),       // Para: Colaborador
                type: 'smoothstep', // Tipo de linha
                animated: false,
                markerEnd: { type: 'arrowclosed', color: '#b1b1b7' },
                style: { stroke: '#b1b1b7', strokeWidth: 2 },
            }));

        // Calcula o layout
        if (initialNodes.length > 0) {
            const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(initialNodes, initialEdges);
            setNodes(layoutedNodes);
            setEdges(layoutedEdges);
        } else {
            setNodes([]);
            setEdges([]);
        }

    }, [selectedArea, allColaboradores, setNodes, setEdges]); // Roda de novo quando a área muda


    return (
        <div className="organograma-container">
            {loading && <LoadingSpinner />}
            
            {/* --- Sidebar de Áreas --- */}
            <div className="organograma-sidebar">
                <div className="sidebar-header">
                    <FaBuilding />
                    <h3>Áreas da Empresa</h3>
                </div>
                <div className="area-list">
                    {areas.map(area => (
                        <button
                            key={area}
                            className={`area-button ${selectedArea === area ? 'active' : ''}`}
                            onClick={() => setSelectedArea(area)}
                        >
                            {area === 'Todos' ? <FaUsers /> : <FaSitemap />}
                            <span>{area}</span>
                        </button>
                    ))}
                </div>
            </div>

            {/* --- Conteúdo Principal (O Organograma) --- */}
            <div className="organograma-content">
                <ReactFlow
                    nodes={nodes}
                    edges={edges}
                    onNodesChange={onNodesChange}
                    onEdgesChange={onEdgesChange}
                    nodeTypes={nodeTypes}
                    fitView
                    nodesDraggable={false} // Trava os nós
                    nodesConnectable={false} // Impede de criar conexões
                    className="react-flow-canvas"
                >
                    <Controls />
                    <Background color="#f0f0f0" variant="dots" gap={16} size={1} />
                </ReactFlow>
            </div>
        </div>
    );
};

export default Organograma;